// WHAT: Exercise step component with timer and optional YouTube video embed
// WHY:  Provides exercise session interface with timer functionality and video guidance
// DOMAIN: Productivity → Miracle Flow → Session Steps
// DEPENDENCIES: Timer component, YouTube embed URL parsing, React state management
// RISKS:
//   - NOTE: YouTube URL parsing uses regex - may fail for non-standard URLs or short links
//   - NOTE: Video embed requires iframe - may have CORS or loading issues
//   - WARN: Timer state is managed externally - may not sync with video playback
//   - NOTE: No validation for video URL format before rendering iframe

import { Zap } from "lucide-react";
import React from "react";

import { Stack, Heading } from "@/components/ui";
import { cn } from "@/shared/utils";

import { Timer } from "../../atoms/Timer";

export interface ExerciseStepProperties {
  className?: string;
  duration: number;
  exerciseVideoUrl?: null | string;
  isActive: boolean;
  onComplete: () => void;
  onStart: () => void;
}

/**
 * WHAT:
 *   Extracts YouTube video ID from various URL formats and converts to embed URL.
 *
 * WHY:
 *   YouTube URLs come in multiple formats (watch, embed, shorts, youtu.be) - this normalizes them.
 *
 * INPUTS:
 *   - url: string | undefined | null — YouTube URL in any format
 *
 * OUTPUT:
 *   YouTube embed URL string or null if URL is invalid/unsupported.
 *
 * RISKS:
 *   - NOTE: Regex pattern may not match all YouTube URL variations
 *   - WARN: No validation of video ID format - may return invalid embed URLs
 *   - NOTE: Short links (youtu.be) may have different validation requirements
 *
 * @param url - YouTube URL in any format
 *
 * @returns YouTube embed URL string or null if URL is invalid/unsupported
 *
 */
function getYouTubeEmbedUrl(url: null | string | undefined): null | string {
  if (!url) {
    return null;
  }

  // NOTE: Regex matches common YouTube URL patterns including shorts and youtu.be
  const match = /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([\w-]{11})/.exec(
    url,
  );
  return match?.[1] ? `https://www.youtube.com/embed/${match[1]}` : null;
}

/**
 * WHAT:
 *   Exercise step component with timer and optional YouTube video embed for workout guidance.
 *
 * WHY:
 *   Provides structured exercise interface with video support and timer tracking for session workflow.
 *
 * PROPS:
 *   - duration: number — Timer duration in minutes
 *   - isActive: boolean — Whether timer is currently running
 *   - exerciseVideoUrl?: string | null — Optional YouTube URL for exercise video
 *   - onStart: () => void — Callback when timer starts
 *   - onComplete: () => void — Callback when timer completes
 *   - className?: string — Optional CSS classes for styling
 *
 * BEHAVIOR:
 *   - Conditionally renders YouTube video iframe if exerciseVideoUrl is provided
 *   - Displays Timer component with progress indicator
 *   - Shows instructional text when timer is not active
 *   - Uses green color scheme to represent energy/activity
 *
 * RISKS:
 *   - NOTE: YouTube URL parsing uses regex - may fail for non-standard URLs or short links
 *   - NOTE: Video embed requires iframe - may have CORS or loading issues
 *   - WARN: Timer state is managed externally - may not sync with video playback
 *   - NOTE: No validation for video URL format before rendering iframe
 *
 * @param root0
 *
 * @param root0.duration
 *
 * @param root0.isActive
 *
 * @param root0.exerciseVideoUrl
 *
 * @param root0.onStart
 *
 * @param root0.onComplete
 *
 * @param root0.className
 *
 * @returns JSX.Element
 *
 */
export const ExerciseStep: React.FC<ExerciseStepProperties> = ({
  className,
  duration,
  exerciseVideoUrl,
  isActive,
  onComplete,
  onStart,
}) => {
  return (
    <div className={cn("space-y-6 w-full", className)}>
      {/* Hero Section */}
      <Stack align="center" className="py-4 w-full" direction="col" gap={4}>
        <div className="flex items-center justify-center w-20 h-20 rounded-tracq bg-gradient-to-br from-green-500 to-green-600 shadow-tracq-card">
          <Zap className="w-10 h-10 text-white" />
        </div>
        <Stack align="center" className="w-full max-w-4xl" direction="col" gap={2}>
          <Heading className="font-bold text-center text-green-400" level="h3" size="xl">
            Exercise
          </Heading>
          <p className="text-[#CBD5E1] text-center text-lg w-full px-4">
            Get your body moving! Follow along with the exercise video or do your own routine.
          </p>
        </Stack>
      </Stack>

      {/* Video Section */}
      {exerciseVideoUrl ? (
        <div className="space-y-3 w-full">
          <label className="block text-sm font-semibold text-[#CBD5E1]">Exercise Video</label>
          <div className="aspect-video bg-[#0F172A] rounded-tracq overflow-hidden border border-[#334155] shadow-tracq-card w-full">
            <iframe
              allowFullScreen
              className="w-full h-full"
              src={getYouTubeEmbedUrl(exerciseVideoUrl) ?? ""}
              title="Exercise Video"
            />
          </div>
        </div>
      ) : null}

      {/* Timer Section */}
      <Stack align="center" className="py-6 w-full" direction="col" gap={4}>
        <Timer
          duration={duration}
          isActive={isActive}
          onComplete={onComplete}
          onStart={onStart}
          showProgress
          size="lg"
        />
        {!isActive && (
          <p className="text-[#94A3B8] text-sm text-center">
            Start the timer when you begin your exercise
          </p>
        )}
      </Stack>
    </div>
  );
};
